// src/features/monetize/components/OverviewTab.tsx
//
// The Monetize dashboard (Revenue Suite Seq 1 step 7): the subscription
// base now, the range's revenue, the starts and cancellations, the trial
// conversion, the paywall funnel and the refused plays. One read per range;
// every number is honest-empty, never a zero for "no data".

import { useState } from 'react';
import { Alert, Button, Group, Paper, SegmentedControl, SimpleGrid, Skeleton, Stack, Switch, Table, Text, Title, useComputedColorScheme } from '@mantine/core';
import { BarChart } from '@mantine/charts';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import { REPORT_PRESETS, REPORT_PRESET_LABELS } from '@/lib/constants/monetize';
import type { MonetizeReport, ReportDay } from '@/lib/types/Monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import { presetRange, useMonetizeReport, type ReportPreset } from '../useMonetizeReport';

export function OverviewTab() {
  const [preset, setPreset] = useState<ReportPreset>('30d');
  const [showTable, setShowTable] = useState(false);
  const { from, to } = presetRange(preset);
  const { report, isLoading, isError, error, refetch } = useMonetizeReport(from, to);
  const scheme = useComputedColorScheme('light');
  // Two categorical hues, validated for both surfaces (dataviz: the orange
  // steps differ per scheme; identity follows the entity across charts).
  const colors = { primary: 'blue.6', secondary: scheme === 'dark' ? 'orange.8' : 'orange.6', neutral: 'gray.6' };

  return (
    <Stack gap="lg">
      <Group justify="space-between" wrap="wrap">
        <SegmentedControl size="xs" data={REPORT_PRESETS.map((p) => ({ value: p, label: REPORT_PRESET_LABELS[p] }))} value={preset} onChange={(v) => setPreset(v as ReportPreset)} />
        <Group gap="sm">
          <Text size="xs" c="dimmed">{MonetizeUtils.rangeLabel(from, to)} · UTC days</Text>
          <Button variant="subtle" size="xs" leftSection={<IconRefresh size={14} />} onClick={() => refetch()} loading={isLoading}>Refresh</Button>
        </Group>
      </Group>

      {isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>{error instanceof Error ? error.message : 'Could not load the dashboard.'}</Alert>
      )}

      <Section title="Right now">
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <Tile label="Monthly recurring revenue" value={report ? MonetizeUtils.formatAmount(report.mrr, report.currency) : null} isLoading={isLoading} secondary={report ? `${report.activeSubscriptions} paying` : undefined} />
          <Tile label="Active subscriptions" value={report ? MonetizeUtils.formatCount(report.activeSubscriptions) : null} isLoading={isLoading} secondary={report && report.pastDue > 0 ? `${report.pastDue} past due` : undefined} />
          <Tile label="Trials in progress" value={report ? MonetizeUtils.formatCount(report.trialing) : null} isLoading={isLoading} />
          <Tile label="Ending at period end" value={report ? MonetizeUtils.formatCount(report.scheduledCancels) : null} isLoading={isLoading} secondary={report && report.paused > 0 ? `${report.paused} paused` : undefined} />
        </SimpleGrid>
      </Section>

      <Section title="This range">
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <Tile label="Revenue" value={report ? MonetizeUtils.formatAmount(report.revenue, report.currency) : null} isLoading={isLoading} secondary={report ? `${MonetizeUtils.formatAmount(report.subscriptionRevenue, report.currency)} subscriptions · ${MonetizeUtils.formatAmount(report.purchaseRevenue, report.currency)} purchases` : undefined} />
          <Tile label="Refunds" value={report ? MonetizeUtils.formatAmount(report.refunds, report.currency) : null} isLoading={isLoading} />
          <Tile label="New subscriptions" value={report ? MonetizeUtils.formatCount(report.subscriptionStarts) : null} isLoading={isLoading} secondary={report ? `${MonetizeUtils.formatCount(report.subscriptionCancels)} canceled` : undefined} />
          <Tile label="Trial conversion" value={report ? MonetizeUtils.trialConversionLabel(report.trials) : null} isLoading={isLoading} secondary={report && report.trials.trials > 0 ? `${report.trials.converted} of ${report.trials.trials} trials paid · ${report.trials.pending} still running` : undefined} emptyCopy="No trials started in this range" />
        </SimpleGrid>
      </Section>

      <Paper withBorder p="md">
        <Group justify="space-between" mb="xs" wrap="wrap">
          <Title order={5}>Revenue by day</Title>
          <Switch size="xs" label="Show table" checked={showTable} onChange={(e) => setShowTable(e.currentTarget.checked)} />
        </Group>
        {isLoading || !report ? (
          <Skeleton height={220} />
        ) : report.revenue === 0 ? (
          <Text size="sm" c="dimmed" py="md">No sales in this range yet.</Text>
        ) : (
          <BarChart
            h={220}
            data={report.days.map((d) => ({ day: MonetizeUtils.dayLabel(d.day), Subscriptions: d.subscriptionRevenue / 100, Purchases: d.purchaseRevenue / 100 }))}
            dataKey="day"
            type="stacked"
            series={[
              { name: 'Subscriptions', color: colors.primary },
              { name: 'Purchases', color: colors.secondary },
            ]}
            withLegend
            legendProps={{ verticalAlign: 'bottom' }}
            gridAxis="y"
            tickLine="y"
            valueFormatter={(v) => MonetizeUtils.formatAmount(Math.round(v * 100), report.currency)}
            barProps={{ radius: [4, 4, 0, 0] }}
            xAxisProps={{ interval: 'preserveStartEnd' }}
          />
        )}
      </Paper>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper withBorder p="md">
          <Title order={5} mb="xs">Subscriptions: new and canceled</Title>
          {isLoading || !report ? (
            <Skeleton height={200} />
          ) : report.subscriptionStarts + report.subscriptionCancels === 0 ? (
            <Text size="sm" c="dimmed" py="md">No subscription changes in this range.</Text>
          ) : (
            <BarChart
              h={200}
              data={report.days.map((d) => ({ day: MonetizeUtils.dayLabel(d.day), New: d.subscriptionStarts, Canceled: d.subscriptionCancels }))}
              dataKey="day"
              series={[
                { name: 'New', color: colors.primary },
                { name: 'Canceled', color: colors.neutral },
              ]}
              withLegend
              legendProps={{ verticalAlign: 'bottom' }}
              gridAxis="y"
              tickLine="y"
              barProps={{ radius: [4, 4, 0, 0] }}
              xAxisProps={{ interval: 'preserveStartEnd' }}
            />
          )}
        </Paper>
        <Paper withBorder p="md">
          <Title order={5} mb="xs">Paywall funnel</Title>
          {isLoading || !report ? (
            <Skeleton height={200} />
          ) : (
            <Funnel report={report} />
          )}
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper withBorder p="md">
          <Title order={5} mb="xs">Top products</Title>
          {isLoading || !report ? (
            <Skeleton height={120} />
          ) : report.topProducts.length === 0 ? (
            <Text size="sm" c="dimmed" py="md">No sales in this range yet.</Text>
          ) : (
            <Table verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Product</Table.Th>
                  <Table.Th ta="right">Sales</Table.Th>
                  <Table.Th ta="right">Revenue</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {report.topProducts.map((p) => (
                  <Table.Tr key={p.productId}>
                    <Table.Td><Text size="sm">{p.name}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{MonetizeUtils.formatCount(p.sales)}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm">{MonetizeUtils.formatAmount(p.revenue, report.currency)}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
        <Paper withBorder p="md">
          <Title order={5} mb="xs">Plays refused</Title>
          {isLoading || !report ? (
            <Skeleton height={120} />
          ) : (
            <Table verticalSpacing="xs">
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td><Text size="sm">Not entitled (saw the paywall)</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{MonetizeUtils.formatCount(report.deniedNoEntitlement)}</Text></Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text size="sm">Too many screens at once</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{MonetizeUtils.formatCount(report.deniedConcurrency)}</Text></Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text size="sm">Other (suspended accounts)</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{MonetizeUtils.formatCount(report.deniedOther)}</Text></Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </SimpleGrid>

      {showTable && report && <DaysTable days={report.days} currency={report.currency} />}
    </Stack>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>{title}</Text>
      {children}
    </Stack>
  );
}

function Tile({ label, value, secondary, isLoading, emptyCopy = 'No data yet' }: { label: string; value: string | null; secondary?: string; isLoading: boolean; emptyCopy?: string }) {
  return (
    <Paper withBorder p="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>{label}</Text>
      {isLoading ? (
        <Skeleton height={28} mt={6} width="60%" />
      ) : value !== null ? (
        <>
          <Text size="xl" fw={600} mt={2}>{value}</Text>
          {secondary && <Text size="xs" c="dimmed" mt={2}>{secondary}</Text>}
        </>
      ) : (
        <Text size="sm" c="dimmed" mt={6}>{emptyCopy}</Text>
      )}
    </Paper>
  );
}

/** Three plain numbers with the step-to-step share: honest, no bars that imply a scale. */
function Funnel({ report }: { report: MonetizeReport }) {
  const hits = report.deniedNoEntitlement;
  const started = report.checkoutsStarted;
  const paid = report.checkoutsCompleted;
  if (hits + started + paid === 0) return <Text size="sm" c="dimmed" py="md">Nobody has reached the paywall in this range.</Text>;
  const rows = [
    { label: 'Saw the paywall', value: hits, share: null as string | null },
    { label: 'Started checkout', value: started, share: MonetizeUtils.percent(started, hits) },
    { label: 'Paid', value: paid, share: MonetizeUtils.percent(paid, started) },
  ];
  return (
    <Table verticalSpacing="xs">
      <Table.Tbody>
        {rows.map((r) => (
          <Table.Tr key={r.label}>
            <Table.Td><Text size="sm">{r.label}</Text></Table.Td>
            <Table.Td ta="right"><Text size="sm" fw={600}>{MonetizeUtils.formatCount(r.value)}</Text></Table.Td>
            <Table.Td ta="right" w={90}><Text size="xs" c="dimmed">{r.share ?? ''}</Text></Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function DaysTable({ days, currency }: { days: ReportDay[]; currency: string }) {
  return (
    <Paper withBorder style={{ overflow: 'auto' }}>
      <Table verticalSpacing="xs" fz="xs" striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Day</Table.Th>
            <Table.Th ta="right">Subscriptions</Table.Th>
            <Table.Th ta="right">Purchases</Table.Th>
            <Table.Th ta="right">Refunds</Table.Th>
            <Table.Th ta="right">New subs</Table.Th>
            <Table.Th ta="right">Canceled</Table.Th>
            <Table.Th ta="right">Paywall</Table.Th>
            <Table.Th ta="right">Checkouts</Table.Th>
            <Table.Th ta="right">Paid</Table.Th>
            <Table.Th ta="right">Too many screens</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {days.map((d) => (
            <Table.Tr key={d.day}>
              <Table.Td>{d.day}</Table.Td>
              <Table.Td ta="right">{MonetizeUtils.formatAmount(d.subscriptionRevenue, currency)}</Table.Td>
              <Table.Td ta="right">{MonetizeUtils.formatAmount(d.purchaseRevenue, currency)}</Table.Td>
              <Table.Td ta="right">{MonetizeUtils.formatAmount(d.refunds, currency)}</Table.Td>
              <Table.Td ta="right">{d.subscriptionStarts}</Table.Td>
              <Table.Td ta="right">{d.subscriptionCancels}</Table.Td>
              <Table.Td ta="right">{d.deniedNoEntitlement}</Table.Td>
              <Table.Td ta="right">{d.checkoutsStarted}</Table.Td>
              <Table.Td ta="right">{d.checkoutsCompleted}</Table.Td>
              <Table.Td ta="right">{d.deniedConcurrency}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}
